"use client"

import { useState, useEffect } from "react"
import { API_BASE_URL } from "../../config"

const ProductModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "Appetizers",
    variations: [{ name: "Regular", price: 0 }],
    modifiers: [],
    image: null,
  })
  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState("")

  const categories = [
    "Appetizers",
    "Salads",
    "Charcoal Grill",
    "Family Platters",
    "Parsley Bowls",
    "Make Your Own Bowl",
    "Sandwiches",
    "Parsley Wraps",
    "Make Your Own Wrap",
    "Pastries",
    "Desserts",
    "Beverages",
  ]

  // Add state for available modifiers and selected modifiers
  const [availableModifiers, setAvailableModifiers] = useState([])
  const [selectedModifiers, setSelectedModifiers] = useState([])

  useEffect(() => {
    if (product) {
      const item_data = product.item_data || {}
      setFormData({
        name: item_data.name || "",
        description: item_data.description_plaintext || item_data.description || "",
        category: item_data.categoryNames?.[0] || "Appetizers",
        variations: item_data.variations?.map((v) => ({
          id: v.id,
          name: v.item_variation_data?.name || "Regular",
          price: v.item_variation_data?.price_money?.amount ? v.item_variation_data.price_money.amount / 100 : 0,
        })) || [{ name: "Regular", price: 0 }],
        modifiers: item_data.modifier_list_info || [],
        image: null,
      })

      // Set selected modifiers
      const currentModifiers = item_data.modifier_list_info?.map((info) => info.modifier_list_id) || []
      setSelectedModifiers(currentModifiers)

      // Set image preview if product has image
      if (item_data.image_ids?.[0]) {
        setImagePreview(`${API_BASE_URL}/api/image/${item_data.image_ids[0]}`)
      }
    }

    fetchAvailableModifiers()
  }, [product])

  // Add the fetchAvailableModifiers function
  const fetchAvailableModifiers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/modifiers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setAvailableModifiers(data.modifiers || [])
      }
    } catch (error) {
      console.error("Error fetching modifiers:", error)
    }
  }

  // Add modifier toggle function
  const toggleModifier = (modifierId) => {
    setSelectedModifiers((prev) => {
      if (prev.includes(modifierId)) {
        return prev.filter((id) => id !== modifierId)
      } else {
        return [...prev, modifierId]
      }
    })
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData({ ...formData, image: file })
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const addVariation = () => {
    setFormData({
      ...formData,
      variations: [...formData.variations, { name: "", price: 0 }],
    })
  }

  const removeVariation = (index) => {
    if (formData.variations.length > 1) {
      const newVariations = formData.variations.filter((_, i) => i !== index)
      setFormData({ ...formData, variations: newVariations })
    }
  }

  const updateVariation = (index, field, value) => {
    const newVariations = [...formData.variations]
    newVariations[index] = { ...newVariations[index], [field]: value }
    setFormData({ ...formData, variations: newVariations })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const submitData = new FormData()
      submitData.append("name", formData.name)
      submitData.append("description", formData.description)
      submitData.append("category", formData.category)
      submitData.append("variations", JSON.stringify(formData.variations))
      submitData.append("modifiers", JSON.stringify(selectedModifiers))

      if (formData.image) {
        submitData.append("image", formData.image)
      }

      const url = product ? `${API_BASE_URL}/api/admin/products/${product.id}` : `${API_BASE_URL}/api/admin/products`

      const method = product ? "PUT" : "POST"

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: submitData,
      })

      if (response.ok) {
        onSave()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to save product")
      }
    } catch (error) {
      console.error("Error saving product:", error)
      alert("Error saving product")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{product ? "Edit Product" : "Add Product"}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />
                {imagePreview && (
                  <img
                    src={imagePreview || "/placeholder.svg"}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded-md"
                  />
                )}
              </div>
            </div>

            {/* Variations */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">Variations *</label>
                <button
                  type="button"
                  onClick={addVariation}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                >
                  Add Variation
                </button>
              </div>

              <div className="space-y-3">
                {formData.variations.map((variation, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border rounded-md">
                    <input
                      type="text"
                      placeholder="Variation name"
                      value={variation.name}
                      onChange={(e) => updateVariation(index, "name", e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    />
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-1">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={variation.price}
                        onChange={(e) => updateVariation(index, "price", Number.parseFloat(e.target.value) || 0)}
                        className="w-20 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </div>
                    {formData.variations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVariation(index)}
                        className="text-red-600 hover:text-red-800 px-2"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modifiers */}
            {availableModifiers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">Assign Modifiers</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-4">
                  {availableModifiers.map((modifier) => {
                    const modifierData = modifier.modifier_list_data || {}
                    const modifierName = modifierData.name || "Unnamed Modifier"
                    const isSelected = selectedModifiers.includes(modifier.id)

                    return (
                      <div key={modifier.id} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id={`modifier-${modifier.id}`}
                          checked={isSelected}
                          onChange={() => toggleModifier(modifier.id)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`modifier-${modifier.id}`} className="text-sm text-gray-700 cursor-pointer">
                          {modifierName}
                          <span className="text-gray-500 ml-1">({modifierData.modifiers?.length || 0} options)</span>
                        </label>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md disabled:opacity-50"
              >
                {loading ? "Saving..." : product ? "Update Product" : "Create Product"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProductModal
